import React, { useEffect, useState } from "react";
import {
  Spinner,
  Modal,
  Button,
  Table,
  Pagination,
  Col,
  Row,
  Card,
} from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useGetActiveCompanies } from "../../api/queries/MuslimInvest/useGetActiveCompanies";
import { CompanyProfileData } from "../Models/CompanyProfileData";
import { BsEye, BsFileArrowDown, BsTrash } from "react-icons/bs";
import { FaFilePdf } from "react-icons/fa";
import { FiBriefcase } from "react-icons/fi";
import { toast } from "react-toastify";
import { useSaveCompanyProfile } from "../../api/mutations/MuslimInvest/useSaveCompanyProfile";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { FoundingTeamData } from "../Models/FoundingTeamData";
import { useDeleteCompany } from "../../api/mutations/MuslimInvest/useDeleteCompany";
import { useSendEmail } from "../../api/mutations/useSendEmail";
import { Users } from "../../types/Types";
import { useGetPitcheByCompanyId } from "../../api/queries/Pitch/useGetPitchByCompanyId";
import { PitchFormData } from "../Models/PitchFormData";
import { useGetPitchFiles } from "../../api/queries/Pitch/useGetPitchFiles";
import { useGetPitchTeamMembers } from "../../api/queries/Pitch/useGetPitchTeamMembers";
import { useTranslation } from "react-i18next";
import { ensureHttpUrlPrefix } from "../../utils/ensureHttpUrlPrefix";
import { useGetCompanyDetails } from "../../api/queries/MuslimInvest/useGetCompanyDetails";
import { useGetCompanyTermsByUser } from "../../api/queries/MuslimInvest/useGetCompanyTermsByUser";
import { useQuery } from "@tanstack/react-query";
import { User } from "../../types/Types";
import {
  AdminDetailSection,
  buildCompanyProfileFields,
  buildCompanyTermsFields,
  buildPitchFields,
  buildPortalUserFields,
  CompanyProfileFilesSection,
} from "./MuslimInvestAdminDetailSections";
import AdminPanelHeader from "./AdminPanelHeader";

const MuslimInvestRequests = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const queryClient = useQueryClient();
  const [foundingTeam, setFoundingTeam] = useState<FoundingTeamData[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyProfileData | null>(null);
  const [filesUploaded, setFilesUploaded] = useState<any[]>([]);

  const { mutate: saveCompanyDetailsMutation } = useSaveCompanyProfile({
    onError: () => {
      toast.error(t("common.unableProcessTryLater"));
    },
  });
  const { data, isPending } = useGetActiveCompanies({ pageSize, page });
  const requests: CompanyProfileData[] = (data?.companies || []).filter(
    (c) => c.status !== "InProgress"
  );
  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const { data: pitchData } = useGetPitcheByCompanyId({
    id: selectedCompany?.id.toString() || "",
    enabled: !!selectedCompany,
  });

  const selectedPitch =
    pitchData && (pitchData.pitches[0]?.pitch as PitchFormData);

  const selectedUserIdStr = selectedCompany?.userId?.toString() ?? "";

  const { data: companyDetailsList, isLoading: companyDetailsLoading } =
    useGetCompanyDetails({
      userId: selectedUserIdStr,
      enabled: showModal && !!selectedUserIdStr,
    });

  const fullCompany =
    companyDetailsList?.find((c) => c.id === selectedCompany?.id) ??
    companyDetailsList?.[0] ??
    selectedCompany;

  const { data: companyTermsList, isLoading: companyTermsLoading } =
    useGetCompanyTermsByUser({
      userId: selectedUserIdStr,
      enabled: showModal && !!selectedUserIdStr,
    });

  const { data: portalUser, isLoading: portalUserLoading } = useQuery({
    queryKey: ["admin-company-portal-user", selectedUserIdStr],
    enabled: showModal && !!selectedUserIdStr,
    queryFn: async () => {
      const res = await axiosInstance.get<Users>(
        getAPIURL(`/User/${selectedUserIdStr}?id=${selectedUserIdStr}`)
      );
      return res.data.user as User;
    },
  });

  const companyProfileFiles = (
    fullCompany as CompanyProfileData & {
      files?: Array<{ id?: number; media?: string }>;
    }
  )?.files;

  const { mutate: sendMail } = useSendEmail({
    onSuccess() {},
  });

  const { data: teamMembersData, isLoading: isTeamLoading } =
    useGetPitchTeamMembers({
      id: selectedPitch?.id.toString() || "",
      enabled: !!selectedPitch,
    });

  const { data: pitchFilesData, isLoading: isFilesLoading } = useGetPitchFiles({
    id: selectedPitch?.id.toString() || "",
    enabled: !!selectedPitch,
  });

  useEffect(() => {
    if (pitchFilesData) {
      setFilesUploaded(pitchFilesData || []);
    }
  }, [pitchFilesData]);

  const downloadFile = async (fileId: number) => {
    try {
      const response = await axiosInstance.get(
        getAPIURL(`/Pitch/pitch-file/${fileId}/download`),
        { responseType: "blob" } // Important to get binary data
      );

      // Extract filename from headers if available
      const contentDisposition = response.headers["content-disposition"];
      const fileName = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/['"]/g, "")
        : `downloaded-file-${fileId}`;

      // Create a URL for the blob
      const fileURL = window.URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const { mutate: deleteCompany } = useDeleteCompany({
    onSuccess: () => {
      toast.success(t("admin.companyDeletedSuccess"));
      setShowDeleteModal(false);
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: () => {
      toast.error(t("admin.companyDeleteError"));
    },
  });

  const openModal = (company: CompanyProfileData) => {
    setSelectedCompany(company);
    setShowModal(true);
    getFoundingTeam(company.userId);
  };
  const closeModal = () => {
    setSelectedCompany(null);
    setShowModal(false);
  };

  const openDeleteModal = (company: CompanyProfileData) => {
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    // Update React Query cache directly
    queryClient.setQueryData(
      ["companies", { pageSize, page }],
      (oldData: any) => {
        if (!oldData) return oldData;
        const updatedCompanies = oldData.companies.map(
          (c: CompanyProfileData) =>
            c.id === id ? { ...c, status: newStatus } : c
        );
        return { ...oldData, companies: updatedCompanies };
      }
    );

    // Update modal if open
    if (selectedCompany?.id === id) {
      setSelectedCompany({ ...selectedCompany, status: newStatus });
    }

    // Call mutation to save status
    const companyToUpdate = requests.find((c) => c.id === id);
    if (!companyToUpdate) return;

    const responseUser = await axiosInstance.get<Users>(
      getAPIURL(`/User/${companyToUpdate.userId}?id=${companyToUpdate.userId}`)
    );

    const formData = new FormData();
    Object.entries(companyToUpdate).forEach(([key, value]) => {
      if (value !== undefined && value !== null)
        formData.append(key, String(value));
    });
    formData.set("status", newStatus);
    saveCompanyDetailsMutation(formData, {
      onSuccess: () => {
        toast.success(t("admin.companyStatusUpdated", { status: newStatus }));
        if (newStatus === "Rejected") {
          sendMail({
            email: responseUser?.data.user.email || "",
            subject: `Your Company Profile Has Been Rejected`,
            name: `${
              responseUser?.data.user.firstName +
                "" +
                responseUser?.data.user.lastName || ""
            }`,
            body: `<!DOCTYPE html>
          <html lang="en">
          <head>
          <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
          <title>Company Profile Status - SMBF</title>
          </head>
          <body style="margin:0; padding:0; font-family:Arial, Helvetica, sans-serif; background-color:#f4f6f8;">
          <table width="100%" border="0" cellspacing="0" cellpadding="0" bgcolor="#f4f6f8">
            <tr>
              <td align="center" style="padding:40px 20px;">
                <table width="600" border="0" cellspacing="0" cellpadding="0" bgcolor="#ffffff"
                  style="border-radius:10px; border:1px solid #e0e0e0; overflow:hidden;">
          
                  <!-- Header -->
                  <tr>
                    <td align="center" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); padding:30px;">
                      <img src="https://drive.google.com/uc?export=view&id=17bPec3b1DFm41gg757oKNyTmP1942MOg"
                        alt="SMBF" width="180"
                        style="max-width:180px; height:auto; display:block; margin:0 auto;" />
                    </td>
                  </tr>
          
                  <!-- Body -->
                  <tr>
                    <td style="padding:35px; text-align:left; color:#333333; font-size:15px; line-height:1.6;">
                      <h2 style="color:#2366CD; font-size:22px; margin:0 0 20px;">
                        Assalamu Alaikum ${
                          responseUser?.data.user.firstName || ""
                        },
                      </h2>
                      <p style="margin:0 0 15px;">
                        We regret to inform you that your company profile submitted to <b>SMBF / Muslim Invest</b> has been rejected.
                      </p>
                      <p style="margin:0 0 15px;">
                        Please review the feedback and contact our support team to resolve any issues or resubmit your profile.
                      </p>
                      <p style="text-align:center;">
                      <a href="https://connect.smbf.global" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); color:#ffffff; padding:14px 28px; font-size:16px; text-decoration:none; border-radius:6px; display:inline-block;">
                      Contact Support
                    </a>
                      </p>
                      <p style="margin:30px 0 0; font-size:13px; color:#555555;">
                        Best regards,<br/>The SMBF Team
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          
            <!-- Footer -->
            <tr>
              <td align="center" style="padding:20px; font-size:11px; color:#888888;">
                &copy; 2025 SMBF. All rights reserved.
              </td>
            </tr>
          </table>
          </body>
          </html>`,
          });
        }
        if (newStatus === "Accepted") {
          sendMail({
            email: responseUser?.data.user.email || "",
            subject: `Your Company Profile Has Been Accepted`,
            name: `${responseUser?.data.user.firstName || ""} ${
              responseUser?.data.user.lastName || ""
            }`,
            body: `
        <table width="100%" cellpadding="0" cellspacing="0" bgcolor="#f4f6f8" style="margin:0; padding:0;">
          <tr>
            <td align="center" style="padding:40px 20px;">
              <table width="600" cellpadding="0" cellspacing="0" bgcolor="#ffffff" style="border-radius:10px; border:1px solid #e0e0e0;">
                <!-- Header -->
                <tr>
                  <td align="center" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); padding:30px;">
                    <img src="https://drive.google.com/uc?export=view&id=17bPec3b1DFm41gg757oKNyTmP1942MOg" alt="SMBF" width="180" style="display:block; margin:0 auto;"/>
                  </td>
                </tr>
                <!-- Body -->
                <tr>
                  <td style="padding:35px; font-family:Arial, Helvetica, sans-serif; color:#333333; font-size:15px; line-height:1.6;">
                    <h2 style="color:#2366CD; font-size:22px; margin:0 0 20px;">
                      Assalamu Alaikum ${
                        responseUser?.data.user.firstName || ""
                      },
                    </h2>
                    <p style="margin:0 0 15px;">
                      Congratulations! Your company profile submitted to <b>SMBF / Muslim Invest</b> has been accepted.
                    </p>
                    <p style="margin:0 0 15px;">
                      Please log in to your account and proceed with the required payment to complete the next steps.
                    </p>
                    <p style="text-align:center;">
                      <a href="https://connect.smbf.global" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); color:#ffffff; padding:14px 28px; font-size:16px; text-decoration:none; border-radius:6px; display:inline-block;">
                        Login & Make Payment
                      </a>
                    </p>
                    <p style="margin:30px 0 0; font-size:13px; color:#555555;">
                      Best regards,<br/>The SMBF Team
                    </p>
                  </td>
                </tr>
                <!-- Footer -->
                <tr>
                  <td align="center" style="padding:20px; font-size:11px; color:#888888;">
                    &copy; 2025 SMBF. All rights reserved.
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
        `,
          });
        }
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleDelete = (id: number) => {
    deleteCompany(id);
  };

  const getFoundingTeam = async (id: string) => {
    try {
      const response = await axiosInstance.get<FoundingTeamData[]>(
        getAPIURL(`/MuslimInvestProfile/FoundingTeam/by-user/${id}`)
      );
      setFoundingTeam(response.data ?? []);
    } catch (err) {
      console.error("Failed to fetch founding team:", err);
    } finally {
      // setLoading(false);
    }
  };

  return (
    <div className="w-100">
      <AdminPanelHeader
        icon={<FiBriefcase size={22} />}
        title="Invest Company Approvals"
        description="Approve or reject Muslim Invest company profiles."
      />

      {isPending && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {!isPending && requests.length === 0 && (
        <div className="alert alert-info text-center">
          No company requests found
        </div>
      )}

      {!isPending && requests.length > 0 && (
        <>
          <Table
            striped
            bordered
            hover
            responsive
            className="rounded-4 shadow-sm"
          >
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>User ID</th>
                <th>Company Name</th>
                <th>Reg. No.</th>
                <th>Description</th>
                <th>Stage</th>
                <th>Status</th>
                <th>Sectors</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((company) => (
                <tr key={company.id}>
                  <td>{company.id}</td>
                  <td>{company.userId ?? "—"}</td>
                  <td>{company.companyName}</td>
                  <td>{company.companyRegistrationNumber || "—"}</td>
                  <td>{company.companyDescription || "—"}</td>
                  <td>{company.developmentStage || "-"}</td>
                  {company.status === "Live" ? (
                    <td>Live</td>
                  ) : (
                    <td>
                      <select
                        className="form-select"
                        value={company.status}
                        onChange={(e) =>
                          handleStatusChange(company.id, e.target.value)
                        }
                      >
                        <option value="Awaiting Approval">
                          Awaiting Approval
                        </option>
                        <option value="Accepted">Accepted</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </td>
                  )}
                  <td>{company.sectorsOfInterest || "-"}</td>
                  <td className="d-flex gap-2">
                    <BsEye
                      size={20}
                      className="text-primary cursor-pointer"
                      onClick={() => openModal(company)}
                    />
                    <BsTrash
                      size={20}
                      className="text-danger cursor-pointer"
                      onClick={() => openDeleteModal(company)}
                      aria-label="Delete company"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.Prev
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              />
              {Array.from({ length: totalPages }, (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === page}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              />
            </Pagination>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCompany && (
            <p>
              Are you sure you want to delete{" "}
              <strong>{selectedCompany.companyDescription}</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            className="rounded-pill"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              selectedCompany?.id && handleDelete(selectedCompany.id)
            }
            className="rounded-pill"
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal */}
      <Modal show={showModal} onHide={closeModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Company Details</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            maxHeight: "75vh",
            overflowY: "auto",
          }}
        >
          {selectedCompany && (
            <div className="p-2">
              {/* Status Dropdown at Top */}
              {selectedCompany.status === "Live" ? (
                <></>
              ) : (
                <select
                  className="form-select"
                  value={selectedCompany.status}
                  onChange={(e) =>
                    handleStatusChange(selectedCompany.id, e.target.value)
                  }
                >
                  <option value="Awaiting Approval">Awaiting Approval</option>
                  <option value="Accepted">Accepted</option>
                  <option value="Rejected">Rejected</option>
                </select>
              )}

              <AdminDetailSection
                title="Portal member"
                fields={buildPortalUserFields(portalUser)}
                loading={portalUserLoading}
              />
              <AdminDetailSection
                title="Company profile"
                fields={buildCompanyProfileFields(fullCompany)}
                loading={companyDetailsLoading}
              />
              <CompanyProfileFilesSection files={companyProfileFiles} />
              {companyTermsLoading && (
                <AdminDetailSection
                  title="Company terms"
                  fields={[]}
                  loading
                />
              )}
              {!companyTermsLoading &&
                (companyTermsList?.length ? (
                  companyTermsList.map((terms, index) => (
                    <AdminDetailSection
                      key={terms.id ?? index}
                      title={
                        companyTermsList.length > 1
                          ? `Company terms #${index + 1}`
                          : "Company terms"
                      }
                      fields={buildCompanyTermsFields(terms)}
                    />
                  ))
                ) : (
                  <AdminDetailSection
                    title="Company terms"
                    fields={[
                      {
                        label: "Terms",
                        value: "No company terms on file",
                      },
                    ]}
                  />
                ))}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Founding Team</h5>
                <div className="row">
                  {Array.isArray(foundingTeam) &&
                    foundingTeam.map((founder) => (
                      <div
                        className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4"
                        key={founder.id ?? `${founder.email}-${Math.random()}`}
                      >
                        <div
                          className="card shadow-sm position-relative border-0"
                          style={{
                            borderRadius: "14px",
                            transition: "0.2s ease-in-out",
                            paddingTop: "45px",
                          }}
                        >
                          <div className="card-body text-center">
                            <h5 className="fw-semibold mb-1">
                              {founder.firstName} {founder.lastName}
                            </h5>

                            <h6
                              className="text-muted mb-3"
                              style={{ fontSize: "0.9rem" }}
                            >
                              {founder.role}
                            </h6>

                            <p className="small mb-1">
                              <strong>Email:</strong>
                              <br />
                              {founder.email}
                            </p>

                            <p className="small mb-0">
                              <strong>LinkedIn:</strong>
                              <br />
                              <a
                                href={ensureHttpUrlPrefix(founder.linkedIn ?? "")}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Profile
                              </a>
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              <AdminDetailSection
                title="Pitch"
                fields={
                  selectedPitch
                    ? buildPitchFields(selectedPitch)
                    : [{ label: "Pitch", value: "No pitch on file for this company" }]
                }
              />

              {/* Team Members */}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Team Members</h5>
                {isTeamLoading ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" /> Loading team
                    members...
                  </div>
                ) : teamMembersData?.teamMembers?.length ? (
                  <Row>
                    {teamMembersData.teamMembers.map((member) => (
                      <Col md={3} sm={6} className="mb-3" key={member.id}>
                        <Card className="shadow-sm border-0 h-100 rounded-3 text-center">
                          <Card.Body>
                            <Card.Title className="fw-semibold mb-1 text-dark">
                              {member.firstName} {member.lastName}
                            </Card.Title>
                            <Card.Subtitle className="text-muted mb-2">
                              {member.role}
                            </Card.Subtitle>
                            <Card.Text className="small text-secondary mb-0">
                              <strong>Experience:</strong>{" "}
                              {member.experience || "N/A"}
                            </Card.Text>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="text-center text-muted">
                    No team members found
                  </div>
                )}
              </div>

              {/* Pitch Files */}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Pitch Files</h5>
                {isFilesLoading ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" /> Loading files...
                  </div>
                ) : filesUploaded.length ? (
                  <Table
                    striped
                    bordered
                    hover
                    responsive
                    className="align-middle shadow-sm"
                  >
                    <thead className="table-light">
                      <tr>
                        <th>File Name</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filesUploaded.map((file) => (
                        <tr key={file.id}>
                          <td>
                            <FaFilePdf className="me-2 text-danger" />
                            {file.name}
                          </td>
                          <td>{file.type}</td>
                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              className="border-0 rounded-circle me-2"
                              style={{ width: "30px", height: "30px" }}
                              onClick={() => downloadFile(file.id)}
                            >
                              <BsFileArrowDown />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted">No files found</div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            className="rounded-pill"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MuslimInvestRequests;
